import { useEffect, useRef, useState, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
interface ScrollExpandMediaProps {
  mediaType?: 'video' | 'image';
  mediaSrc: string;
  posterSrc?: string;
  bgImageSrc: string;
  title?: string;
  date?: string;
  scrollToExpand?: string;
  textBlend?: boolean;
  children?: ReactNode;
}
const ScrollExpandMedia = ({
  mediaType = 'video',
  mediaSrc,
  posterSrc,
  bgImageSrc,
  title,
  date,
  scrollToExpand,
  textBlend,
  children
}: ScrollExpandMediaProps) => {
  const [isInView, setIsInView] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaContainerRef = useRef<HTMLDivElement | null>(null);

  // Intersection Observer to detect when section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsInView(true);
        } else {
          setIsInView(false);
          // Pause when out of view
          if (videoRef.current && mediaType === 'video') {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        }
      });
    }, {
      threshold: 0.5
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [mediaType]);
  const firstWord = title ? title.split(' ')[0] : '';
  const restOfTitle = title ? title.split(' ').slice(1).join(' ') : '';
  const togglePlayPause = () => {
    if (videoRef.current) {
      setHasInteracted(true);
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  const handleVideoEnded = () => {
    setIsPlaying(false);
    setHasInteracted(true);
  };
  return <div ref={sectionRef} className='transition-colors duration-700 ease-in-out overflow-x-hidden'>
    <section className='relative flex flex-col items-center justify-start min-h-[100dvh]'>
      <div className='relative w-full flex flex-col items-center min-h-[100dvh]'>
        <div className='absolute inset-0 z-0 h-full'>
          <img src={bgImageSrc} alt='Background' className='w-screen h-screen object-cover object-center' loading='eager' />
          <div className='absolute inset-0 bg-black/10' />
        </div>

        <div className='container mx-auto flex flex-col items-center justify-start relative z-10'>
          <div className='flex flex-col items-center justify-center w-full h-[100dvh] relative'>
            <div ref={mediaContainerRef} className='absolute z-0 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-none rounded-lg sm:rounded-2xl overflow-hidden w-[70vw] sm:w-[50vw] md:w-[40vw] lg:w-[30vw] max-w-[500px]' style={{
              aspectRatio: '9/16',
              maxHeight: '85vh',
              boxShadow: '0px 0px 50px rgba(0, 0, 0, 0.3)'
            }}>
              {mediaType === 'video' ? mediaSrc.includes('youtube.com') ? <div className='relative w-full h-full pointer-events-none'>
                <iframe width='100%' height='100%' src={mediaSrc.includes('embed') ? mediaSrc + (mediaSrc.includes('?') ? '&' : '?') + 'autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&disablekb=1&modestbranding=1' : mediaSrc.replace('watch?v=', 'embed/') + '?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&disablekb=1&modestbranding=1&playlist=' + mediaSrc.split('v=')[1]} className='w-full h-full rounded-xl' frameBorder='0' allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture' allowFullScreen />
                <div className='absolute inset-0 z-10' style={{
                  pointerEvents: 'none'
                }}></div>
              </div> : <div className='relative w-full h-full pointer-events-none'>
                <video ref={videoRef} src={mediaSrc} poster={posterSrc} playsInline preload='metadata' onEnded={handleVideoEnded} className='w-full h-full object-contain rounded-lg sm:rounded-xl' style={{
                  pointerEvents: 'none'
                }} />

                {/* Play Button Overlay */}
                {!isPlaying && <div className='absolute inset-0 z-20 flex items-center justify-center pointer-events-auto'>
                  <Button
                    size="lg"
                    onClick={togglePlayPause}
                    className="
    relative
    h-16 w-16 sm:h-20 sm:w-20
    rounded-full
    bg-background/30
    backdrop-blur-xs
    border border-white/20
    overflow-hidden
    shadow-[inset_0_2px_6px_rgba(255,255,255,0.25),0_6px_20px_rgba(0,0,0,0.25)]
    transition-all duration-300
    hover:bg-background/20
  "
                  >
                    {/* convex highlight (top-left light reflection) */}
                    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-white/25 to-transparent opacity-60 pointer-events-none" />

                    {/* convex shadow (bottom-right depth) */}
                    <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-black/20 to-transparent opacity-40 pointer-events-none" />

                    {/* inner light refraction rim */}
                    <span className="absolute inset-0 rounded-full shadow-[inset_0_1px_3px_rgba(255,255,255,0.3)] pointer-events-none" />

                    <Play className="h-8 w-8 sm:h-10 sm:w-10 text-white/90 relative z-10" />
                  </Button>
                </div>}

                {/* Video Controls */}
                {isPlaying && <div className='absolute bottom-2 right-2 sm:bottom-4 sm:right-4 flex gap-2 z-20 pointer-events-auto'>
                  <Button size="sm" variant="secondary" onClick={togglePlayPause} className="bg-background/80 backdrop-blur-sm hover:bg-background/90 h-8 w-8 sm:h-10 sm:w-10 p-0">
                    <Pause className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                  <Button size="sm" variant="secondary" onClick={toggleMute} className="bg-background/80 backdrop-blur-sm hover:bg-background/90 h-8 w-8 sm:h-10 sm:w-10 p-0">
                    {isMuted ? <VolumeX className="h-3 w-3 sm:h-4 sm:w-4" /> : <Volume2 className="h-3 w-3 sm:h-4 sm:w-4" />}
                  </Button>
                </div>}
                <div className='absolute inset-0 z-10' style={{
                  pointerEvents: 'none'
                }}></div>
              </div> : <div className='relative w-full h-full'>
                <img src={mediaSrc} alt={title || 'Media content'} className='w-full h-full object-contain rounded-lg sm:rounded-xl' />
              </div>}

            </div>

            <div className={`flex items-center justify-center text-center gap-4 w-full relative z-10 transition-none flex-col ${textBlend ? 'mix-blend-difference' : 'mix-blend-normal'}`}>

            </div>
          </div>


        </div>
      </div>
    </section>
  </div>;
};
export default ScrollExpandMedia;